/**
 * Wishlist Functionality for Shopify Dawn Theme
 * – localStorage-based, no backend required
 * – Event delegation handles AJAX-filtered collection pages
 * – MutationObserver (on <main>) re-syncs heart states after filter/sort swaps
 * – Wishlist page renders cards using Dawn's EXACT card-product HTML structure
 */

const WISHLIST_KEY = 'shopify_dawn_wishlist';

/* ─────────────────────────────────────────────
   Data Layer
───────────────────────────────────────────── */
const Wishlist = {
  getItems() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch { return []; }
  },

  saveItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  },

  has(productId) {
    return this.getItems().some((i) => String(i.id) === String(productId));
  },

  add(product) {
    const items = this.getItems();
    if (!this.has(product.id)) { items.push(product); this.saveItems(items); }
  },

  remove(productId) {
    this.saveItems(this.getItems().filter((i) => String(i.id) !== String(productId)));
  },

  toggle(product) {
    if (this.has(product.id)) { this.remove(product.id); return false; }
    this.add(product); return true;
  },

  count() { return this.getItems().length; },
  clear() { localStorage.removeItem(WISHLIST_KEY); },
};

/* ─────────────────────────────────────────────
   UI Helpers
───────────────────────────────────────────── */
function setButtonState(btn, isActive) {
  btn.classList.toggle('wishlist-btn--active', isActive);
  btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
  btn.setAttribute('aria-pressed', String(isActive));
  const icon = btn.querySelector('.wishlist-icon');
  if (icon) icon.setAttribute('fill', isActive ? 'currentColor' : 'none');
}

function updateWishlistCounter() {
  const count = Wishlist.count();
  document.querySelectorAll('.wishlist-count-bubble').forEach((el) => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function syncAllButtonStates() {
  // Read localStorage once and build a Set for O(1) lookups
  const ids = new Set(Wishlist.getItems().map((i) => String(i.id)));
  document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
    const id = btn.dataset.productId;
    if (id) setButtonState(btn, ids.has(String(id)));
  });
}

/* ─────────────────────────────────────────────
   Event Delegation
   One listener on document catches all wishlist
   buttons — including AJAX-injected ones.
───────────────────────────────────────────── */
function initWishlistDelegation() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const product = {
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
      available: btn.dataset.productAvailable !== 'false',
      onSale: btn.dataset.productOnSale === 'true',
      compareAtPrice: btn.dataset.productCompareAtPrice || '',
    };

    if (!product.id) return;

    const added = Wishlist.toggle(product);
    setButtonState(btn, added);
    updateWishlistCounter();
    showToast(
      added
        ? `"${product.title}" added to wishlist`
        : `"${product.title}" removed from wishlist`
    );
  });
}

/* ─────────────────────────────────────────────
   MutationObserver
   Watches <main> (never replaced by Dawn AJAX)
   and re-syncs heart states after the product
   grid is swapped by filters / sorting.
───────────────────────────────────────────── */
function observeProductGrid() {
  const target = document.querySelector('main') || document.body;
  let debounce = null;

  new MutationObserver((mutations) => {
    if (!mutations.some((m) => m.addedNodes.length > 0)) return;
    clearTimeout(debounce);
    debounce = setTimeout(syncAllButtonStates, 200);
  }).observe(target, { childList: true, subtree: true });
}

/* ─────────────────────────────────────────────
   Toast
───────────────────────────────────────────── */
let toastTimer = null;

function showToast(message) {
  let toast = document.getElementById('wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('wishlist-toast--visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('wishlist-toast--visible'), 3000);
}

/* ─────────────────────────────────────────────
   Dawn Card HTML Builder
   Replicates card-product.liquid output exactly
   so wishlist cards are visually identical to
   every other product card in the theme.
───────────────────────────────────────────── */
function buildDawnCardHTML(item) {
  const s = window.wishlistDawnSettings || {};
  const cardStyle = s.cardStyle || 'card';
  const colorScheme = s.cardColorScheme || 'scheme-2';
  const badgePosition = s.badgePosition || 'bottom left';
  const soldOutScheme = s.soldOutScheme || 'inverse';
  const saleScheme = s.saleScheme || 'accent-2';
  const soldOutLabel = s.soldOutLabel || 'Sold out';
  const onSaleLabel = s.onSaleLabel || 'Sale';

  const isCard = cardStyle === 'card';
  const isStandard = cardStyle === 'standard';

  /* Outer card classes — mirrors Dawn's Liquid logic exactly */
  const cardClasses = [
    'card',
    `card--${cardStyle}`,
    'card--media',
    isCard ? `color-${colorScheme} gradient` : '',
  ].filter(Boolean).join(' ');

  /* Inner div classes */
  const innerClasses = [
    'card__inner',
    isStandard ? `color-${colorScheme} gradient` : '',
    'ratio',
  ].filter(Boolean).join(' ');

  /* Badge HTML */
  let badgeHTML = '';
  if (!item.available) {
    badgeHTML = `<span class="badge badge--bottom-left color-${soldOutScheme}">${escapeHtml(soldOutLabel)}</span>`;
  } else if (item.onSale) {
    badgeHTML = `<span class="badge badge--bottom-left color-${saleScheme}">${escapeHtml(onSaleLabel)}</span>`;
  }

  /* Price HTML — mirrors Dawn's price snippet structure */
  const priceHTML = item.onSale
    ? `<div class="price price--on-sale">
        <div class="price__container">
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last">${(item.price)}</span>
          </div>
          <div class="price__compare">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <s class="price-item price-item--regular">${(item.compareAtPrice)}</s>
          </div>
        </div>
      </div>`
    : `<div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price-item price-item--regular">${(item.price)}</span>
          </div>
        </div>
      </div>`;

  /* Wishlist remove button (heart icon — reuses existing CSS) */
  const heartBtnHTML = `
    <button
      type="button"
      class="wishlist-btn wishlist-btn--active"
      data-wishlist-btn
      data-product-id="${item.id}"
      data-product-title="${escapeAttr(item.title)}"
      data-product-url="${escapeAttr(item.url)}"
      data-product-image="${escapeAttr(item.image)}"
      data-product-price="${escapeAttr(item.price)}"
      data-product-available="${item.available}"
      data-product-on-sale="${item.onSale}"
      data-product-compare-at-price="${escapeAttr(item.compareAtPrice)}"
      aria-label="Remove from wishlist"
      aria-pressed="true"
    >
      <svg class="wishlist-icon" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="currentColor" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true" focusable="false">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>`;

  /* Image HTML */
  const imageHTML = item.image
    ? `<img
        src="${escapeAttr(item.image)}"
        alt="${escapeAttr(item.title)}"
        class="motion-reduce"
        loading="lazy"
        width="533"
        height="533"
      >`
    : `<div class="media media--placeholder">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
          <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c-.1-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/>
        </svg>
      </div>`;

  /* Full card — mirrors card-product.liquid structure */
  return `
    <div class="card-wrapper product-card-wrapper underline-links-hover">
      <div class="${cardClasses}" style="--ratio-percent: 100%;">
        <div class="${innerClasses}" style="--ratio-percent: 100%;">
          <div class="card__media">
            <div class="media media--transparent media--hover-effect">
              <a href="${escapeAttr(item.url)}" aria-label="${escapeAttr(item.title)}">
                ${imageHTML}
              </a>
            </div>
            ${heartBtnHTML}
          </div>
        </div>

        <div class="card__content">
          <div class="card__information">
            <h3 class="card__heading h5" id="title-wishlist-${item.id}">
              <a
                href="${escapeAttr(item.url)}"
                id="CardLink-wishlist-${item.id}"
                class="full-unstyled-link"
                aria-labelledby="CardLink-wishlist-${item.id} Badge-wishlist-${item.id}"
              >
                ${escapeHtml(item.title)}
              </a>
            </h3>
            <div class="card-information">
              ${priceHTML}
            </div>
          </div>
          <div class="card__badge ${badgePosition}" id="Badge-wishlist-${item.id}">
            ${badgeHTML}
          </div>
        </div>
      </div>
    </div>`;
}


/* ─────────────────────────────────────────────
   Wishlist Page Renderer
───────────────────────────────────────────── */
function autoInjectWishlistContainer() {
  if (document.getElementById('wishlist-page-container')) return;
  if (!window.location.pathname.includes('/pages/wishlist')) return;

  const rte = document.querySelector(
    'main .rte, main [class*="page-width"] .rte, main .page-width'
  );
  if (!rte) return;

  rte.innerHTML = `
    <div id="wishlist-page-container" style="padding:4rem 0;">
      <div class="wishlist-page__header" style="margin-bottom:3.2rem;">
        <h1 style="font-size:clamp(2.4rem,4vw,3.6rem);font-family:var(--font-heading-family);margin:0 0 .8rem;">My Wishlist</h1>
        <p id="wishlist-count-text" style="font-size:1.4rem;color:rgba(var(--color-foreground),.6);margin:0;"></p>
      </div>
      <div class="wishlist-empty" id="wishlist-empty-state"
        style="display:flex;flex-direction:column;align-items:center;gap:2rem;padding:6rem 2rem;text-align:center;">
        <svg style="width:5.6rem;height:5.6rem;color:rgba(var(--color-foreground),.2)"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <h2 style="font-size:2rem;font-family:var(--font-heading-family);margin:0;">Your wishlist is empty</h2>
        <p style="font-size:1.4rem;color:rgba(var(--color-foreground),.55);margin:0;max-width:36rem;">
          Save your favourite products here and come back to them anytime.
        </p>
        <a href="/collections/all" class="button">Start Shopping</a>
      </div>
      <ul id="wishlist-grid" class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul>
    </div>`;

  const countEl = document.getElementById('wishlist-count-text');
  if (countEl) {
    const n = Wishlist.count();
    countEl.textContent = n === 0 ? '' : (n === 1 ? '1 item' : `${n} items`);
  }
}

function renderWishlistPage() {
  autoInjectWishlistContainer();

  const container = document.getElementById('wishlist-page-container');
  if (!container) return;

  const items = Wishlist.getItems();
  const emptyState = document.getElementById('wishlist-empty-state');
  const grid = document.getElementById('wishlist-grid');

  if (!items.length) {
    if (emptyState) emptyState.style.display = 'flex';
    if (grid) grid.innerHTML = '';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  if (grid) {
    /* Wrap each card in a <li class="grid__item"> — exactly like collection pages */
    grid.innerHTML = items
      .map((item) => `<li class="grid__item">${buildDawnCardHTML(item)}</li>`)
      .join('');

    /* When a wishlist button inside the page is toggled (remove), re-render */
    grid.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      /* State handled by delegation; we only need to watch for removal to re-render */
      btn.addEventListener('click', () => {
        /* Give delegation time to update localStorage first */
        requestAnimationFrame(() => {
          renderWishlistPage();
          updateWishlistCounter();
        });
      });
    });
  }
}

/* ─────────────────────────────────────────────
   Utilities
───────────────────────────────────────────── */
const _escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(str) {
  return (str || '').replace(/[&<>"']/g, (c) => _escapeMap[c]);
}

function escapeAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* ─────────────────────────────────────────────
   Backward-compat alias
───────────────────────────────────────────── */
function initWishlistButtons() { syncAllButtonStates(); }

/* ─────────────────────────────────────────────
   Bootstrap
───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initWishlistDelegation();  // global delegated click handler
  syncAllButtonStates();     // fill hearts for already-wishlisted items
  updateWishlistCounter();   // header badge
  observeProductGrid();      // re-sync after AJAX filter / sort / pagination
  renderWishlistPage();      // render Dawn-native cards on /pages/wishlist
});

document.addEventListener('shopify:section:load', () => {
  syncAllButtonStates();
  updateWishlistCounter();
});
